import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { projectsApi, suppliersApi, projectSuppliersApi, projectGroupsApi, usersApi } from '@/services/api';
import { Project } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { Check, AlertTriangle } from 'lucide-react';
import HoverTooltip from '@/components/HoverTooltip';

// Build a display label for a user — name if set, otherwise email
const userDisplayName = (u: { firstName?: string | null; lastName?: string | null; email: string }) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email;

// RD traffic light: green = every collection fully protected, amber = a mix,
// red = none (or no collections configured).
const RD_STATUS_META: Record<'red' | 'amber' | 'green', { color: string; label: string }> = {
  green: { color: '#10B981', label: 'All collections fully protected' },
  amber: { color: '#F59E0B', label: 'Some collections not fully protected' },
  red: { color: '#EF4444', label: 'No collections fully protected' },
};
const RdStatusLight = ({
  status,
  breakdown,
}: {
  status?: 'red' | 'amber' | 'green';
  breakdown?: { collections: number; fullyProtected: number; rdEnabled: number; duplicates: number; geo: number };
}) => {
  const meta = RD_STATUS_META[status || 'red'];
  const n = breakdown?.collections ?? 0;
  const row = (label: string, value: string) => (
    <div className="flex items-center justify-between gap-4">
      <span className="text-white/60">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
  const content = (
    <div className="space-y-1">
      <div className="font-semibold mb-1">{meta.label}</div>
      {n === 0 ? (
        <div className="text-white/60">No collections configured</div>
      ) : (
        <>
          {row('Fully protected', `${breakdown!.fullyProtected} / ${n}`)}
          {row('RD enabled', `${breakdown!.rdEnabled} / ${n}`)}
          {row('Block duplicates', `${breakdown!.duplicates} / ${n}`)}
          {row('Geo blocker', `${breakdown!.geo} / ${n}`)}
        </>
      )}
    </div>
  );
  return (
    <HoverTooltip content={content}>
      <span
        className="inline-block w-3 h-3 rounded-full cursor-help"
        style={{ backgroundColor: meta.color }}
      />
    </HoverTooltip>
  );
};

const ProjectsPage = () => {
  const [error, setError] = useState<string>('');

    // Title keyword box; filters the loaded project list by title, client-side
  const [keywordInput, setKeywordInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Project Manager filter: 'all' | 'mine' | 'unassigned' | a user id
  const [pmFilter, setPmFilter] = useState('all');
  // Group filter: 'all' | 'ungrouped' | a group id
  const [groupFilter, setGroupFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);

  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const { data: response, isLoading, error: queryError } = useQuery(
    ['projects', { status: statusFilter }],
    () => projectsApi.getAll({
      status: statusFilter || undefined,
    }),
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      // Keep the current cards on screen while a new search/filter fetches, so
      // only the results swap in — the page no longer flashes the full skeleton.
      keepPreviousData: true,
      onError: (error) => {
        setError('Failed to load projects');
        console.error('Projects query error:', error);
      },
    }
  );

  // Platform-wide project + respondent totals for the header stat.
  const { data: statsResponse } = useQuery(
    'project-stats',
    () => projectsApi.getStats(),
    { refetchInterval: 30000 }
  );
  const totalRespondents: number | undefined = statsResponse?.data?.totalRespondents;
  const respondentWarning: boolean = statsResponse?.data?.respondentWarning === true;
  const respondentWarningThreshold: number | undefined = statsResponse?.data?.respondentWarningThreshold;

  // Fetch suppliers data to map to projects
  const { data: suppliersResponse } = useQuery(
    'suppliers',
    () => suppliersApi.getAll(),
    {
      onError: (error) => {
        console.error('Suppliers query error:', error);
      },
    }
  );

  // CR-03: project groups
  const { data: groupsResponse } = useQuery(
    'project-groups',
    () => projectGroupsApi.getAll(),
    {
      onError: (error) => {
        console.error('Project groups query error:', error);
      },
    }
  );

  // Users (id/email) for mapping the Project Manager to a display name
  const { data: usersResponse } = useQuery(
    'users-selectable',
    () => usersApi.getSelectable(),
    {
      onError: (error) => {
        console.error('Users query error:', error);
      },
    }
  );

  const deleteProjectMutation = useMutation(projectsApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('projects');
    },
  });

  const allProjects = response?.data as Project[] || [];
  const suppliers = suppliersResponse?.data || [];
  const projectGroups = (groupsResponse?.data || []) as Array<{ id: string; name: string }>;
  const pagination = (response as any)?.pagination;

  // Apply the Title keyword filter (matches project titles only), the Project
  // Manager filter (All / Mine / a specific user / Unassigned) and the Group
  // filter (All / Ungrouped / a specific group). All three combine.
  const titleQuery = keywordInput.trim().toLowerCase();
  const projects = allProjects.filter(p => {
    if (titleQuery && !(p.name || '').toLowerCase().includes(titleQuery)) return false;

    const pmMatch =
      pmFilter === 'all' ? true :
      pmFilter === 'unassigned' ? !p.projectManagerId :
      pmFilter === 'mine' ? p.projectManagerId === currentUser?.id :
      p.projectManagerId === pmFilter;
    if (!pmMatch) return false;

    if (groupFilter === 'all') return true;
    if (groupFilter === 'ungrouped') return !p.projectGroupId;
    return p.projectGroupId === groupFilter;
  });

  // CR-03: map group IDs to names so each card can show its group inline
  const groupMap = projectGroups.reduce((acc: Record<string, string>, group) => {
    acc[group.id] = group.name;
    return acc;
  }, {});

  // Create a map of supplier IDs to names for easy lookup
  const supplierMap = suppliers.reduce((acc: Record<string, string>, supplier: any) => {
    acc[supplier.id] = supplier.name;
    return acc;
  }, {});

  // Map user IDs to a display label for the Project Manager — name if set, else email
  const users = (usersResponse?.data || []) as Array<{ id: string; email: string; role: string; firstName?: string | null; lastName?: string | null }>;
  const userMap = users.reduce((acc: Record<string, string>, u) => {
    acc[u.id] = userDisplayName(u);
    return acc;
  }, {});

  // Managers who are assigned to at least one project — used to populate the
  // manager filter so people with no projects are not listed (derived from the
  // full, unfiltered project list so the options stay stable across filtering).
  const assignedManagerIds = new Set(
    allProjects.map(p => p.projectManagerId).filter(Boolean) as string[]
  );
  const managersWithProjects = users.filter(u => assignedManagerIds.has(u.id));

  const handleDeleteProject = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      deleteProjectMutation.mutate(id);
    }
  };

  const getStatusColor = (status: string) => {
    const currentStatus = status || 'open';
    return currentStatus === 'open'
      ? 'bg-[#18A5F9] text-white'
      : 'bg-[#E2E5EA] text-[#6B7280]';
  };

  // CR-03: a single project card — reused for grouped boxes and ungrouped projects
  const renderProjectCard = (project: Project, index: number) => (
    <div
      key={project.id}
      className={`md-card p-6 animate-fade-in-up stagger-${Math.min(index + 1, 6)}`}
    >
      {/* Card Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 pr-3">
          <h3 className="text-lg font-bold font-heading mb-1">
            <Link
              to={`/admin/projects/${project.id}`}
              className="gradient-text hover:opacity-80 transition-opacity"
            >
              {project.name}
            </Link>
          </h3>
          <p className="text-[13px] text-[#9CA3AF] font-mono">
            {project.projectId}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {project.linksKilled && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
              <span className="w-2 h-2 rounded-full bg-red-600" />
              KILLED
            </span>
          )}
          {project.projectGroupId && groupMap[project.projectGroupId] && (
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#EDE9FE] text-[#6D28D9]">
              GROUPED
            </span>
          )}
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${getStatusColor(project.status)}`}>
            {(project.status || 'open').toUpperCase()}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="space-y-2.5 mb-5 text-sm">
        {project.debugRedirects && (
          <div className="flex items-center gap-1.5 text-amber-600 text-xs font-semibold">
            <AlertTriangle className="w-3.5 h-3.5" />
            Debug Mode
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-[#9CA3AF]">Project Manager</span>
          <span className="font-semibold text-[#374151] truncate ml-3">
            {(project.projectManagerId && userMap[project.projectManagerId]) || 'Unassigned'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[#9CA3AF]">Group</span>
          <span className="font-semibold text-[#374151] truncate ml-3">
            {(project.projectGroupId && groupMap[project.projectGroupId]) || 'None'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[#9CA3AF]">Completes</span>
          <span className="font-semibold text-[#374151]">
            {project.completes ?? 0}
            {project.completesTarget ? (
              <span className="font-semibold"> / {project.completesTarget}</span>
            ) : null}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[#9CA3AF]">ResearchDefender Status</span>
          <RdStatusLight status={project.rdStatus} breakdown={project.rdBreakdown} />
        </div>
      </div>

      {/* Card Actions */}
      <div className="flex gap-2 pt-4 border-t border-[#ECEEF1]">
        <Link
          to={`/admin/projects/${project.id}`}
          className="md-btn md-btn-primary flex-1 text-center justify-center text-[13px]"
        >
          View Project
        </Link>
        <button
          onClick={() => handleDeleteProject(project.id, project.name)}
          className="md-btn md-btn-danger text-[13px] px-4"
        >
          Delete
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <div className="h-9 w-48 md-skeleton mb-2"></div>
            <div className="h-5 w-32 md-skeleton"></div>
          </div>
          <div className="h-11 w-36 md-skeleton rounded-[10px]"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-[14px] border border-[#ECEEF1] p-6 space-y-4">
              <div className="h-6 md-skeleton w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 md-skeleton w-full"></div>
                <div className="h-4 md-skeleton w-5/6"></div>
                <div className="h-4 md-skeleton w-2/3"></div>
              </div>
              <div className="flex gap-2 pt-4">
                <div className="h-10 md-skeleton flex-1 rounded-[10px]"></div>
                <div className="h-10 md-skeleton w-20 rounded-[10px]"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || queryError) {
    return (
      <div className="text-center py-20">
        <div className="text-[#EF4444] text-lg mb-4 font-medium">
          {error || 'Failed to load projects. Please try again.'}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="md-btn md-btn-primary"
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-end animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">Projects</h1>
          <p className="text-[#6B7280] text-[15px]">
            {projects.length} {projects.length === 1 ? 'project' : 'projects'}
            {totalRespondents !== undefined && (
              <>
                {' · '}
                <span className={respondentWarning ? 'font-semibold text-amber-700' : ''}>
                  {totalRespondents.toLocaleString()} {totalRespondents === 1 ? 'respondent' : 'respondents'}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowGroupModal(true)}
            className="md-btn md-btn-outlined"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New Group
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="md-btn md-btn-primary"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            New Project
          </button>
        </div>
      </div>

      {/* Respondent-volume early warning. Fires below the danger zone so there is
          runway to act (extract rd_threat_score column, precompute supplier
          aggregates, archive closed projects) before scale destabilises the app. */}
      {respondentWarning && totalRespondents !== undefined && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 animate-fade-in-up"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-[14px] text-amber-900">
            <p className="font-semibold">
              Respondent volume is high — {totalRespondents.toLocaleString()} rows
              {respondentWarningThreshold !== undefined && (
                <> (warning threshold {respondentWarningThreshold.toLocaleString()})</>
              )}
            </p>
            <p className="mt-0.5 text-amber-800">
              The respondents table is approaching the size where reporting starts to
              strain the application. Plan the respondent-table optimisations now —
              extracting the RD threat score into a column, precomputing supplier
              metrics, and archiving closed projects — before it destabilises.
            </p>
          </div>
        </div>
      )}

      {/* Keyword + Project Manager + Group filters */}
      <div className="flex flex-wrap items-center gap-6 animate-fade-in-up stagger-1">
        <div className="flex items-center gap-3">
          <label className="text-[13px] font-bold text-[#374151] uppercase tracking-wider">Title</label>
          <div className="md-outlined-textfield">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              placeholder="Search project titles…"
              className="min-w-[220px]"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-[13px] font-bold text-[#374151] uppercase tracking-wider">Manager</label>
          <div className="md-outlined-textfield">
            <select
              value={pmFilter}
              onChange={(e) => setPmFilter(e.target.value)}
              className="min-w-[220px]"
            >
              <option value="all">All managers</option>
              {currentUser?.id && <option value="mine">Mine</option>}
              <option value="unassigned">Unassigned</option>
              {managersWithProjects.length > 0 && (
                <optgroup label="People">
                  {managersWithProjects.map(u => (
                    <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-[13px] font-bold text-[#374151] uppercase tracking-wider">Group</label>
          <div className="md-outlined-textfield">
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="min-w-[220px]"
            >
              <option value="all">All groups</option>
              <option value="ungrouped">Ungrouped</option>
              {projectGroups.length > 0 && (
                <optgroup label="Groups">
                  {projectGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        </div>
      </div>


      {/* Projects Cards */}
      {projects.length === 0 ? (
        <div className="text-center py-20 animate-fade-in-up stagger-2">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#E8F4FD] flex items-center justify-center">
            <svg className="w-8 h-8 text-[#18A5F9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
          </div>
          <p className="text-lg text-[#6B7280] mb-6">
            {(keywordInput.trim() || pmFilter !== 'all' || groupFilter !== 'all')
              ? 'No projects match the current filters.'
              : 'No projects yet. Create your first one to get started.'}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="md-btn md-btn-primary"
          >
            Create Your First Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((project, index) => renderProjectCard(project, index))}
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries('projects');
          }}
        />
      )}

      {/* CR-03: Create Project Group Modal */}
      {showGroupModal && (
        <CreateGroupModal onClose={() => setShowGroupModal(false)} />
      )}
    </div>
  );
};

// CR-03: Create Project Group Modal
const CreateGroupModal = ({ onClose }: { onClose: () => void }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [created, setCreated] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Group name is required');
      return;
    }
    setIsLoading(true);
    try {
      const res = await projectGroupsApi.create(name.trim());
      if (res.success) {
        setCreated(true);
      } else {
        setError(res.error || 'Failed to create group');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create group');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-2">New Project Group</h2>
        <p className="text-sm text-[#6B7280] mb-6">
          Projects assigned to the same group share cross-project duplicate detection.
        </p>

        {created ? (
          <div className="space-y-5">
            <div className="text-emerald-700 text-sm bg-emerald-50 p-3 border border-emerald-200 rounded-xl">
              Group "{name.trim()}" created. Assign projects to it from each project's edit screen.
            </div>
            <button onClick={onClose} className="md-btn md-btn-primary w-full">Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
            )}
            <div>
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Group Name</label>
              <div className="md-outlined-textfield">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full"
                  placeholder="e.g. Q4 2026 Tracker"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="md-btn md-btn-outlined flex-1">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
                {isLoading ? 'Creating…' : 'Create Group'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Create Project Modal Component
const CreateProjectModal = ({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    notes: '',
    genericLink: '',
    redirectUrl: '',
    allowIdReuse: false,
    projectManagerId: '',
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [createdProject, setCreatedProject] = useState<any>(null);
  const [currentDomain] = useState(window.location.origin);
  const [copiedButton, setCopiedButton] = useState<string | null>(null);

  // Users for the Project Manager select; defaults to the current user
  const { data: usersResponse } = useQuery('users-selectable', () => usersApi.getSelectable());
  const users = (usersResponse?.data || []) as Array<{ id: string; email: string; role: string; firstName?: string | null; lastName?: string | null }>;

  // Default the Project Manager to the current user once known (unless already chosen)
  useEffect(() => {
    if (user?.id) {
      setFormData(prev => (prev.projectManagerId ? prev : { ...prev, projectManagerId: user.id }));
    }
  }, [user?.id]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    setError('');

    try {
      const projectData = {
        name: formData.name,
        notes: formData.notes || undefined,
        genericLink: formData.genericLink || undefined,
        redirectUrl: formData.redirectUrl || undefined,
        allowIdReuse: formData.allowIdReuse,
        projectManagerId: formData.projectManagerId || undefined,
      };

      const result = await projectsApi.create(projectData as any);
      if (result.success) {
        setCreatedProject(result.data);
      } else {
        setError(result.error || 'Failed to create project');
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create project');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleClose = () => {
    setCreatedProject(null);
    setFormData({
      name: '',
      notes: '',
      genericLink: '',
      redirectUrl: '',
      allowIdReuse: false,
      projectManagerId: user?.id || '',
    });
    onClose();
    onSuccess();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const copyUrlWithFallback = (title: string, url: string, buttonType: 'individual' | 'all' = 'individual', identifier?: string) => {
    const textToCopy = `${title}\n${url}`;
    copyToClipboard(textToCopy);

    setCopiedButton(buttonType === 'all' ? 'all' : identifier || 'all');

    setTimeout(() => {
      setCopiedButton(null);
    }, 2000);
  };

  const copyAllUrlsWithFallback = () => {
    const allUrls = (urls as any[]).map(({ title, path }: any) => {
      const url = `${currentDomain}/${path}?projectid=${createdProject.projectId}&mid=XXXX`;
      return `${title}\n${url}`;
    }).join('\n\n');
    copyToClipboard(allUrls);

    setCopiedButton('all');

    setTimeout(() => {
      setCopiedButton(null);
    }, 2000);
  };

  const urls = [
    { title: 'Complete', path: 'complete' },
    { title: 'Terminate', path: 'terminate' },
    { title: 'Quota', path: 'quota' },
    { title: 'Security', path: 'security' }
  ];

  // If project was created, show URLs screen
  if (createdProject) {

    return (
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-4xl shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1] max-h-[90vh] overflow-y-auto">
          <div className="text-center mb-6">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#ECFDF5] flex items-center justify-center">
              <Check className="w-6 h-6 text-[#10B981]" />
            </div>
            <h2 className="text-2xl font-bold font-heading gradient-text mb-2">
              Project Created!
            </h2>
            <p className="text-[#6B7280] text-sm">
              <span className="font-mono font-semibold text-[#1A1F2B]">{createdProject.projectId}</span>
              {' '}&middot;{' '}{createdProject.name}
            </p>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-bold text-[#1A1F2B]">Your Project URLs</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  copyAllUrlsWithFallback();
                }}
                className="md-btn md-btn-primary text-xs px-4 py-2 min-h-0 h-8"
              >
                {copiedButton === 'all' ? 'Copied!' : 'Copy All'}
              </button>
            </div>
            <div className="space-y-2">
              {urls.map(({ title, path }) => {
                const url = `${currentDomain}/${path}?projectid=${createdProject.projectId}&mid=XXXX`;
                return (
                  <div key={path} className="bg-[#FAFBFC] border border-[#ECEEF1] rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-bold text-[#6B7280] uppercase tracking-wider">{title}</span>
                      <code className="text-xs text-[#374151] block font-mono truncate mt-0.5">
                        {url}
                      </code>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyUrlWithFallback(title, url, 'individual', path);
                      }}
                      className="text-xs font-semibold text-[#18A5F9] hover:text-[#0D8FDE] transition-colors flex-shrink-0"
                    >
                      {copiedButton === path ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-4 mb-6">
            <p className="text-sm text-[#1E40AF]">
              Replace <code className="font-mono font-bold bg-white/50 px-1.5 py-0.5 rounded">{'XXXX'}</code> with the actual member ID when sending respondents.
            </p>
          </div>

          <button
            onClick={handleClose}
            className="md-btn md-btn-primary w-full"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Show creation form
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1]">
        <h2 className="text-2xl font-bold font-heading gradient-text mb-6">Create New Project</h2>
        <form onSubmit={handleSubmit} className="space-y-4">

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Project Name *</label>
            <div className="md-outlined-textfield">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Project Manager</label>
            <div className="md-outlined-textfield">
              <select
                name="projectManagerId"
                value={formData.projectManagerId}
                onChange={handleChange}
                className="w-full"
              >
                <option value="">Unassigned</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{userDisplayName(u)}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-500 mt-1">Defaults to you; choose another user if needed.</p>
          </div>

          <div>
            <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Notes</label>
            <div className="md-outlined-textfield">
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full"
                placeholder="Add any project notes..."
              />
            </div>
          </div>

          <div className="border-t pt-4 supplier-redirect-settings">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Supplier Redirect Settings</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-2">Generic Link</label>
                <div className="md-outlined-textfield">
                  <input
                    type="url"
                    name="genericLink"
                    value={formData.genericLink}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="https://example.com/generic-survey-link"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Optional: Generic survey link for suppliers</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Redirect URL</label>
                <div className="md-outlined-textfield">
                  <input
                    type="url"
                    name="redirectUrl"
                    value={formData.redirectUrl}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="https://supplier.com/redirect"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Optional: URL to redirect respondents after survey</p>
              </div>

              <div className="flex space-x-6">
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="allowIdReuse"
                      checked={formData.allowIdReuse}
                      onChange={handleChange}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Allow ID Reuse</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">Allow multiple uses of the same ID</p>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={handleClose} className="md-btn md-btn-outlined flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="md-btn md-btn-primary flex-1">
              {isLoading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectsPage;
